'use client'

import Image from 'next/image'

interface PageHeaderProps {
  title: string
  description?: string
  breadcrumb?: { label: string; href?: string }[]
}

export default function PageHeader({ title, description, breadcrumb }: PageHeaderProps) {
  return (
    <section className="relative min-h-[280px] md:min-h-[320px] overflow-hidden">
      {/* Gradient Arka Plan */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            linear-gradient(145deg, 
              #dbeafe 0%, 
              #eff6ff 25%, 
              #f0f9ff 50%, 
              #e0f2fe 75%, 
              #dbeafe 100%
            )
          `,
        }}
      />
      
      {/* Radial gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 50%, rgba(96,165,250,0.1) 0%, transparent 50%)
          `,
        }}
      />
      
      {/* Dekoratif Blob'lar */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Sol blob */}
        <div className="absolute -top-16 -left-16 w-[280px] h-[280px] animate-float-slow opacity-70">
          <Image
            src="/images/decorations/blob-1.svg"
            alt=""
            fill
            className="object-contain"
          />
        </div>
        
        {/* Sağ blob */}
        <div className="absolute -top-10 -right-10 w-[220px] h-[220px] animate-float-medium opacity-65">
          <Image
            src="/images/decorations/blob-2.svg"
            alt=""
            fill
            className="object-contain"
          />
        </div>
        
        {/* Küçük daireler */}
        <div className="absolute top-1/2 left-1/4 w-4 h-4 bg-primary-400/25 rounded-full animate-pulse-soft" />
        <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-primary-300/30 rounded-full animate-float-fast hidden md:block" />
        <div className="absolute bottom-1/4 right-1/4 w-5 h-5 bg-primary-500/20 rounded-full animate-float-medium" />
        
        {/* Hafif grid */}
        <div 
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59,130,246,1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59,130,246,1) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
      </div>
      
      {/* İçerik */}
      <div className="relative z-10 container-custom pt-24 pb-16 md:pt-28 md:pb-20">
        <div className="text-center max-w-3xl mx-auto">
          {/* Breadcrumb */}
          {breadcrumb && breadcrumb.length > 0 && (
            <nav className="mb-4 animate-fade-in" aria-label="Breadcrumb">
              <ol className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <li>
                  <a href="/" className="hover:text-primary-600 transition-colors">
                    Anasayfa
                  </a>
                </li>
                {breadcrumb.map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="text-gray-400">/</span>
                    {item.href ? (
                      <a href={item.href} className="hover:text-primary-600 transition-colors">
                        {item.label}
                      </a>
                    ) : (
                      <span className="text-gray-700 font-medium">{item.label}</span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}
          
          {/* Başlık */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 animate-fade-in">
            {title}
          </h1>
          
          {/* Açıklama */}
          {description && (
            <p className="text-lg md:text-xl text-gray-600 animate-slide-up max-w-2xl mx-auto">
              {description}
            </p>
          )}
        </div>
      </div>
      
      {/* Alt dalga geçişi */}
      <div className="absolute bottom-0 left-0 right-0 w-full h-[60px] md:h-[80px]">
        <svg 
          viewBox="0 0 1440 80" 
          preserveAspectRatio="none" 
          className="w-full h-full"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="pageHeaderWave" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#93c5fd', stopOpacity: 0.2 }} />
              <stop offset="50%" style={{ stopColor: '#bfdbfe', stopOpacity: 0.3 }} />
              <stop offset="100%" style={{ stopColor: '#93c5fd', stopOpacity: 0.2 }} />
            </linearGradient>
          </defs>
          <path 
            fill="url(#pageHeaderWave)" 
            d="M0,40 C320,60 640,20 960,40 C1120,50 1280,30 1440,45 L1440,80 L0,80 Z"
          />
          <path 
            fill="#ffffff" 
            d="M0,55 C240,45 480,65 720,50 C960,35 1200,60 1440,50 L1440,80 L0,80 Z"
          />
        </svg>
      </div>
    </section>
  )
}

